import { Component, computed, ElementRef, inject, signal, viewChild } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';

import { IconComponent } from '@reforgium/icon';
import { LangPipe, LangService, RouteWatcher, ThemeService } from '@reforgium/presentia';
import { SkButtonComponent } from '@ss/ui';

import { urls } from 'constants/urls.constant';

import { AuthService } from 'use-cases/auth.service';

import { BreadcrumbsComponent } from './breadcrumbs.component';

@Component({
  selector: 'app-header',
  host: { '(keydown.escape)': 'dismissMenu()' },
  imports: [IconComponent, BreadcrumbsComponent, RouterLink, RouterLinkActive, LangPipe, SkButtonComponent],
  template: `
    <header class="nav-container">
      <nav class="header-main-nav flex gap-4 min-w-0">
        <a [routerLink]="['/']" class="logo-link shrink-0" aria-label="Салык Кенеш">
          <re-icon name="../logo/logo" width="36" height="36" color="var(--header-accent)" />
        </a>

        <div class="flex flex-col justify-center min-w-0">
          <span class="brand-title hidden sm:block">Салык Кенеш</span>
          <span class="breadcrumbs-mobile">{{ title() || 'Салык Кенеш' }}</span>
        </div>

        @if (auth.isAuthenticated) {
          <nav class="section-nav" aria-label="Основная навигация">
            <a
              [routerLink]="['/']"
              routerLinkActive="active"
              [routerLinkActiveOptions]="{ exact: true }"
              ariaCurrentWhenActive="page"
              (click)="closeMenu()"
            >
              {{ 'document.navigation.label' | lang }}
            </a>
            <a
              [routerLink]="['/', urls.users]"
              [style.display]="auth.canManageUsers ? null : 'none'"
              routerLinkActive="active"
              ariaCurrentWhenActive="page"
              (click)="closeMenu()"
            >
              {{ 'users.navigation.label' | lang }}
            </a>
            <a
              [routerLink]="['/', urls.dashboard]"
              routerLinkActive="active"
              ariaCurrentWhenActive="page"
              (click)="closeMenu()"
            >
              {{ 'dashboard.navigation.label' | lang }}
            </a>
            <a
              [routerLink]="['/', urls.aiSalyk]"
              [style.display]="auth.canManageUsers ? null : 'none'"
              routerLinkActive="active"
              ariaCurrentWhenActive="page"
              (click)="closeMenu()"
            >
              {{ 'ai-salyk.navigation.label' | lang }}
            </a>
            <a
              [routerLink]="['/', urls.logs]"
              [style.display]="auth.canManageUsers ? null : 'none'"
              routerLinkActive="active"
              ariaCurrentWhenActive="page"
              (click)="closeMenu()"
            >
              {{ 'audit.navigation.label' | lang }}
            </a>
          </nav>
        }
      </nav>

      <button
        type="button"
        #menuButton
        aria-label="Меню"
        aria-controls="header-actions"
        [attr.aria-expanded]="menuOpen()"
        class="burger-btn"
        (click)="menuOpen.set(!menuOpen())"
      >
        <re-icon [name]="menuOpen() ? 'cross' : 'menu'" width="24" height="24" color="var(--header-accent)" />
      </button>

      <div id="header-actions" class="nav-actions" [class.open]="menuOpen()">
        @if (auth.isAuthenticated) {
          <nav class="section-nav section-nav-mobile" aria-label="Основная навигация">
            <a
              [routerLink]="['/']"
              routerLinkActive="active"
              [routerLinkActiveOptions]="{ exact: true }"
              ariaCurrentWhenActive="page"
              (click)="closeMenu()"
            >
              {{ 'document.navigation.label' | lang }}
            </a>
            <a
              [routerLink]="['/', urls.dashboard]"
              routerLinkActive="active"
              ariaCurrentWhenActive="page"
              (click)="closeMenu()"
            >
              {{ 'dashboard.navigation.label' | lang }}
            </a>
            <a
              [routerLink]="['/', urls.aiSalyk]"
              [style.display]="auth.canManageUsers ? null : 'none'"
              routerLinkActive="active"
              ariaCurrentWhenActive="page"
              (click)="closeMenu()"
            >
              {{ 'ai-salyk.navigation.label' | lang }}
            </a>
            <a
              [routerLink]="['/', urls.users]"
              [style.display]="auth.canManageUsers ? null : 'none'"
              routerLinkActive="active"
              ariaCurrentWhenActive="page"
              (click)="closeMenu()"
            >
              {{ 'users.navigation.label' | lang }}
            </a>
            <a
              [routerLink]="['/', urls.logs]"
              [style.display]="auth.canManageUsers ? null : 'none'"
              routerLinkActive="active"
              ariaCurrentWhenActive="page"
              (click)="closeMenu()"
            >
              {{ 'audit.navigation.label' | lang }}
            </a>
          </nav>
        }

        <div class="nav-actions-main">
          @if (auth.isAuthenticated && !auth.canManageUsers) {
            <sk-button
              icon="plus"
              appearance="secondary"
              [label]="'administrative.buttons.addDocument' | lang"
              (clicked)="handleAdd(); closeMenu()"
            />
          }
          <button
            type="button"
            class="language-toggle"
            style="gap:8px;padding-inline:10px;font-size:var(--app-button-font-size)"
            [attr.aria-label]="currentLanguage() === 'ru' ? 'Кыргызча' : 'Русский'"
            [attr.title]="currentLanguage() === 'ru' ? 'Кыргызча' : 'Русский'"
            (click)="toggleLanguage()"
          >
            {{ currentLanguage() === 'ru' ? 'Русский' : 'Кыргызча' }}
            <re-icon name="chevron-thin-down" width="12" height="12" />
          </button>
          <div class="theme-toggle" role="group" aria-label="Тема оформления">
            <button
              type="button"
              class="theme-option"
              [class.selected-theme]="themeService.isDark()"
              [attr.aria-pressed]="themeService.isDark()"
              aria-label="Тёмная тема"
              (click)="setTheme(true)"
            >
              <re-icon
                name="moon"
                width="18"
                height="18"
                [color]="themeService.isDark() ? '#2da5fa' : 'var(--header-accent)'"
              />
            </button>
            <button
              type="button"
              class="theme-option"
              [class.selected-theme]="!themeService.isDark()"
              [attr.aria-pressed]="!themeService.isDark()"
              aria-label="Светлая тема"
              (click)="setTheme(false)"
            >
              <re-icon
                name="sun"
                width="18"
                height="18"
                [color]="!themeService.isDark() ? '#2da5fa' : 'var(--header-accent)'"
              />
            </button>
          </div>
        </div>

        @if (auth.isAuthenticated) {
          <button class="logout-btn" (click)="logout(); closeMenu()">
            <re-icon name="logout" width="20" height="20" color="var(--header-accent)" />
            {{ 'login.logout' | lang }}
          </button>
        }
      </div>
    </header>
    <div class="breadcrumb-bar">
      <app-breadcrumbs />
    </div>
  `,
  styles: `
    .brand-title {
      font-size: calc(14px * var(--a11y-font-scale, 1));
      font-weight: 400;
    }
    .header-main-nav {
      gap: 12px;
    }
    .theme-option {
      display: grid;
      place-items: center;
      flex: 0 0 26px;
      width: 26px;
      height: 26px;
      padding: 0;
      border: 1px solid transparent;
      border-radius: 6px;
      background: transparent;
      color: var(--header-accent);
      cursor: pointer;
    }
    .theme-option.selected-theme {
      background: var(--glass-selected-surface);
      border-color: transparent;
      box-shadow: var(--glass-edge);
    }
    .theme-option:focus-visible {
      outline: 2px solid #2da5fa;
      outline-offset: 2px;
    }
    .theme-option:hover {
      background: rgb(255 255 255 / 10%);
    }
    :host {
      position: relative;
      z-index: 1000;
      display: block;
      flex: 0 0 auto;
    }

    .nav-container {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.375rem;
      box-sizing: border-box;
      width: 100%;
      max-width: 100%;
      min-height: 56px;
      padding: 8px 4.2%;
      background-color: #191d33;
      color: var(--header-accent);

      @media screen and (max-width: 1400px) {
        padding-inline: 4.2%;
      }

      @media screen and (max-width: 768px) {
        padding: 8px 16px;
      }
    }

    .breadcrumb-bar {
      display: flex;
      align-items: center;
      box-sizing: border-box;
      width: fit-content;
      max-width: 91.6%;
      padding: 2px 12px 8px 12px;
      margin: 0 4.2%;
      background: var(--surface-background);
      border-radius: 0 0 25px 25px;
    }

    .breadcrumb-bar app-breadcrumbs {
      display: block;
      min-width: 0;
      overflow-x: auto;
      scrollbar-width: none;
    }

    .breadcrumb-bar app-breadcrumbs::-webkit-scrollbar {
      display: none;
    }

    @media screen and (max-width: 768px) {
      .breadcrumb-bar {
        width: 100%;
        max-width: 100%;
        margin-inline: 0;
        padding: 8px 16px;
      }
    }

    .logo-link {
      display: grid;
      place-items: center;
      min-width: 44px;
      min-height: 44px;
    }

    .breadcrumbs-mobile {
      display: none;
      font-size: 1rem;
      font-weight: 600;
      color: var(--header-accent);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .header-main-nav {
      flex: 1 1 auto;
      min-width: 0;
      overflow: visible;
    }

    .section-nav {
      position: absolute;
      top: 50%;
      left: 50%;
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-left: 0;
      transform: translate(-50%, -50%);

      a {
        display: inline-flex;
        align-items: center;
        min-height: 34px;
        padding: 0.25rem 0.5rem;
        border: 1px solid transparent;
        color: rgb(255 255 255 / 0.78);
        font-size: var(--font-size-sm);
        font-weight: var(--fw-medium);
        line-height: 1;
        text-decoration: none;
        white-space: nowrap;
        transition:
          color 150ms ease,
          background-color 150ms ease;

        &:hover {
          color: var(--header-accent);
        }

        &.active {
          color: var(--header-accent);
          border-color: transparent;
          background: #263353;
          border-radius: var(--radius-md);
          font-weight: var(--fw-semibold);
        }
      }
    }

    .section-nav-mobile {
      display: none;
    }

    .nav-actions {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      gap: 0.75rem;
      flex-wrap: wrap;
      justify-content: flex-end;
    }

    .language-toggle,
    .theme-toggle {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-height: 32px;
      height: auto;
      padding: 0.4rem;
      border: 0;
      border-radius: var(--radius-md);
      background: #263353;
      color: var(--header-accent);
      cursor: pointer;
      transition:
        background-color 150ms ease,
        opacity 150ms ease;

      &:hover {
        opacity: 0.9;
      }

      &:focus-visible {
        outline: 2px solid var(--header-accent);
        outline-offset: 2px;
      }
    }

    .theme-toggle {
      gap: 2px;
      width: 62px;
      order: -1;
      padding: 2px;
      box-sizing: border-box;
    }
    .nav-actions-main {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    .burger-btn {
      display: none;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      flex-shrink: 0;
      border: none;
      background: transparent;
      cursor: pointer;
    }

    .logout-btn {
      @apply flex justify-center items-center gap-2 p-2;
      height: 32px;
      border: 0;
      font-size: var(--app-button-font-size);
      border-radius: var(--radius-md);
      background: #263353;
      color: var(--header-accent);
    }

    .language-toggle,
    .theme-toggle,
    .logout-btn {
      border: 1px solid transparent;
      background: var(--glass-header-surface);
      backdrop-filter: var(--glass-blur);
      -webkit-backdrop-filter: var(--glass-blur);
      box-shadow: var(--glass-edge);
    }
    @media screen and (max-width: 1100px) {
      .burger-btn {
        display: flex;
      }

      .breadcrumbs-mobile {
        display: block;
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
        max-width: calc(100vw - 160px);
        text-align: center;
      }

      .section-nav {
        display: none;
      }

      .section-nav-mobile {
        position: static;
        display: flex;
        width: 100%;
        flex-wrap: wrap;
        margin-left: 0;
        padding-bottom: 0.125rem;
        border-bottom: 1px solid rgb(255 255 255 / 0.18);
        transform: none;

        a {
          flex: 1;
          justify-content: center;
        }
      }

      .nav-actions {
        display: none;
        flex-direction: column;
        gap: 0.5rem;
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        padding: 0.75rem;
        background-color: #191d33;
        box-shadow: var(--box-shadow);
        z-index: 100;

        &.open {
          display: flex;
        }

        .nav-actions-main {
          flex-wrap: wrap;
          justify-content: space-between;
          gap: 0.375rem;
        }

        .logout-btn {
          flex-shrink: 0;
          justify-content: center;
          width: 100%;
        }
      }
    }
  `,
})
export class HeaderComponent {
  protected auth = inject(AuthService);
  protected watcher = inject(RouteWatcher);
  protected readonly title = this.watcher.selectData<string>('title');
  private readonly router = inject(Router);

  protected readonly urls = urls;
  protected readonly menuOpen = signal(false);
  private readonly menuButton = viewChild.required<ElementRef<HTMLButtonElement>>('menuButton');

  protected readonly langService = inject(LangService);
  protected readonly themeService = inject(ThemeService);
  protected readonly currentLanguage = computed(() => (this.langService.currentLang() === 'ky' ? 'ky' : 'ru'));

  toggleLanguage(): void {
    this.langService.setLang(this.currentLanguage() === 'ru' ? 'ky' : 'ru');
  }

  setTheme(dark: boolean): void {
    if (this.themeService.isDark() !== dark) this.themeService.switch();
  }
  closeMenu() {
    this.menuOpen.set(false);
  }

  handleAdd() {
    void this.router.navigate([urls.documentCreation]);
  }

  logout() {
    this.auth.logout();
  }
  protected dismissMenu(): void {
    if (!this.menuOpen()) return;
    this.closeMenu();
    this.menuButton().nativeElement.focus();
  }
}
